import React, { useEffect, useState } from 'react';
import { Menu, X, KeyRound } from 'lucide-react';
import { Button } from './ui/button';
import { useLang } from '../context/LangContext';
import Logo from './Logo';
import LangSwitcher from './LangSwitcher';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { label: t('nav.home'), href: '#inicio' },
    { label: t('nav.about'), href: '#nosotros' },
    { label: t('nav.services'), href: '#servicios' },
    { label: t('nav.pricing'), href: '#tarifas' },
    { label: t('nav.works'), href: '#trabajos' },
    { label: t('nav.contact'), href: '#contacto' },
  ];

  const handleClick = (href) => {
    setOpen(false);
    if (location.pathname !== '/') {
      navigate('/' + href);
      setTimeout(() => {
        document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || open
          ? 'bg-[#0a1420]/90 backdrop-blur-xl border-b border-white/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between gap-3">
        {/* Logo */}
        <button onClick={() => handleClick('#inicio')} className="flex items-center group flex-shrink-0">
          <Logo size="md" />
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7 xl:gap-8">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => handleClick(l.href)}
              className="text-[13.5px] text-white/70 hover:text-white transition-colors tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <LangSwitcher />
          <Button
            className="bg-[#00e0c7] hover:bg-[#00c4ad] text-[#0d1b2a] font-semibold rounded-full px-5 h-10 shadow-lg shadow-cyan-500/10"
            onClick={() => handleClick('#contacto')}
          >
            <KeyRound className="w-4 h-4 mr-2" />
            {t('cta.clientAccess')}
          </Button>
        </div>

        {/* Mobile right side */}
        <div className="lg:hidden flex items-center gap-2">
          <LangSwitcher compact />
          <button className="text-white p-1" onClick={() => setOpen(!open)} aria-label="menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#0a1420]/95 backdrop-blur-xl border-t border-white/5">
          <div className="px-5 py-5 flex flex-col gap-1">
            {links.map((l) => (
              <button
                key={l.href}
                onClick={() => handleClick(l.href)}
                className="text-left text-white/80 hover:text-[#00e0c7] py-3 border-b border-white/5 text-[15px]"
              >
                {l.label}
              </button>
            ))}
            <Button
              className="bg-[#00e0c7] hover:bg-[#00c4ad] text-[#0d1b2a] font-semibold rounded-full mt-4"
              onClick={() => handleClick('#contacto')}
            >
              <KeyRound className="w-4 h-4 mr-2" />
              {t('cta.clientAccess')}
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
